/**
 * Dynamic Quiz Components
 *
 * These wrapper components fetch quiz configuration from Firestore (via RemoteQuizService)
 * and render the appropriate templates. This enables pushing new quizzes without app updates.
 *
 * Components:
 * - DynamicQuizLanding: Renders QuizLandingTemplate with remote config
 * - DynamicQuizQuestion: Renders UniversalQuestionPage with remote config
 * - DynamicQuizResults: Renders ResultsTemplate with remote config
 */

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ClimbingBoxLoader } from "react-spinners";

import QuizLandingTemplate from "./QuizLandingTemplate";
import ResultsTemplate from "./ResultsTemplate";
import UniversalQuestionPage from "./UniversalQuestionPage";
import remoteQuizService from "../services/RemoteQuizService";
import { Black, OffWhite } from "../components/theme/colors";

// Loading component for quiz data fetch
const QuizLoadingState = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      background: Black,
    }}
  >
    <ClimbingBoxLoader color={OffWhite} />
  </div>
);

// Not found component for invalid quiz types
const QuizNotFound = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      background: "#000",
      color: "#fff",
      flexDirection: "column",
    }}
  >
    <h1 style={{ marginBottom: "10px" }}>Quiz Not Found</h1>
    <p>The quiz you're looking for doesn't exist.</p>
    <a
      href="/home"
      style={{ color: "#F951FF", marginTop: "20px", textDecoration: "none" }}
    >
      Go to Home
    </a>
  </div>
);

/**
 * DynamicQuizLanding
 *
 * Fetches quiz config from RemoteQuizService and renders QuizLandingTemplate.
 * Falls back to static config if remote fetch fails.
 */
export const DynamicQuizLanding = () => {
  const { quizType } = useParams();
  const [quizConfig, setQuizConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        // Try remote config first
        const config = await remoteQuizService.getQuizConfig(quizType);

        if (config) {
          setQuizConfig(config);
        } else {
          setNotFound(true);
        }
      } catch (error) {
        console.error("Error fetching quiz config:", error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [quizType]);

  if (loading) {
    return <QuizLoadingState />;
  }

  if (notFound) {
    return <QuizNotFound />;
  }

  return (
    <QuizLandingTemplate
      quizConfig={quizConfig}
      specialHeaderConfig={quizConfig.specialHeaderConfig}
      hasNativePlatformLogic={quizConfig.hasNativePlatformLogic}
    />
  );
};

/**
 * DynamicQuizQuestion
 *
 * Renders UniversalQuestionPage directly for dynamic quizzes.
 * UniversalQuestionPage already supports fetching config and questions from RemoteQuizService.
 */
export const DynamicQuizQuestion = () => {
  const { quizType } = useParams();

  // UniversalQuestionPage handles its own loading, error states, and remote data fetching
  return <UniversalQuestionPage quizType={quizType} />;
};

/**
 * DynamicQuizResults
 *
 * Fetches quiz config from RemoteQuizService and renders ResultsTemplate.
 * Falls back to static config if remote fetch fails.
 */
export const DynamicQuizResults = () => {
  const { quizType } = useParams();
  const [quizConfig, setQuizConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const config = await remoteQuizService.getQuizConfig(quizType);

        if (config) {
          setQuizConfig(config);
        } else {
          setNotFound(true);
        }
      } catch (error) {
        console.error("Error fetching quiz config:", error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [quizType]);

  if (loading) {
    return <QuizLoadingState />;
  }

  if (notFound) {
    return <QuizNotFound />;
  }

  return <ResultsTemplate quizConfig={quizConfig} />;
};

const DynamicQuizComponents = {
  DynamicQuizLanding,
  DynamicQuizQuestion,
  DynamicQuizResults,
};

export default DynamicQuizComponents;
